import { useQuery } from '@vue/apollo-composable';
import { computed, type ComputedRef } from 'vue';

import type { MoneyV2 } from '@/__generated__/storefront/graphql';
import { collectionProductsQuery } from '@/shared/graphql/storefront/queries/collection.queries';
import type { StorefrontCollectionProduct } from '@/shared/graphql/storefront/types';
import { getCountryCode } from '@/shared/utils/country';
import { shopifyGidToId } from '@/shared/utils/shopify/id-formatter';

export type TransformedProduct = {
  id: string;
  title: string;
  handle: string;
  variantId: number;
  variantTitle?: string;
  price: MoneyV2;
  compareAtPrice: MoneyV2 | null;
  imageUrl: string;
  imageAlt: string;
  availableForSale: boolean;
};

/**
 * Transforms a Storefront API product into a flattened format for easier consumption.
 * Uses product-level availableForSale for inclusion, then selects the best variant for display.
 *
 * @param storefrontProduct - Product from Storefront API
 * @returns Flattened product with selected variant details, or null if product unavailable
 */
const transformStorefrontProduct = (
  storefrontProduct: StorefrontCollectionProduct
): TransformedProduct | null => {
  if (!storefrontProduct.availableForSale) return null;

  const variants = storefrontProduct.variants.nodes;
  if (!variants.length) return null;

  const availableVariants = variants.filter((v) => v.availableForSale);
  const selectedVariant =
    availableVariants.find((v) => v.image) ||
    availableVariants[0] ||
    variants.find((v) => v.image) ||
    variants[0]; // Fallback to first variant if none available in fetched set

  if (!selectedVariant) return null;

  const productId = shopifyGidToId(storefrontProduct.id);
  const variantId = Number.parseInt(shopifyGidToId(selectedVariant.id), 10);

  if (Number.isNaN(variantId)) return null;

  const image = selectedVariant.image || storefrontProduct.featuredImage;
  const imageUrl = image?.url || '';
  const imageAlt =
    image?.altText || storefrontProduct.featuredImage?.altText || '';

  const price = selectedVariant.price;
  const compareAtPrice = selectedVariant.compareAtPrice ?? null;

  return {
    id: productId,
    title: storefrontProduct.title,
    handle: storefrontProduct.handle,
    variantId,
    price,
    compareAtPrice,
    imageUrl,
    imageAlt,
    availableForSale: selectedVariant.availableForSale ?? true
  };
};

/**
 * Composable for fetching and transforming products from a Shopify collection.
 * Handles GraphQL query, transformation to flattened format, and pagination.
 *
 * @param collectionHandle - Shopify collection handle
 * @param options - Configuration options
 * @returns Transformed products, loading state, error, and pagination methods
 */
export const useStorefrontCollectionProducts = (
  collectionHandle: string,
  options?: {
    enabled?: ComputedRef<boolean>;
    first?: number;
    variantsLimit?: number;
  }
) => {
  const { result, loading, error, refetch, fetchMore } = useQuery(
    collectionProductsQuery,
    computed(() => ({
      handle: collectionHandle,
      country: getCountryCode(),
      first: options?.first ?? 10,
      after: null,
      variantsLimit: options?.variantsLimit ?? 10
    })),
    {
      clientId: 'storefront',
      enabled:
        options?.enabled ??
        computed(() => !!collectionHandle && collectionHandle.length > 0)
    }
  );

  const collection = computed(() => {
    return result.value?.collectionByHandle ?? null;
  });

  const products = computed(() => {
    const collectionData = collection.value;
    if (!collectionData?.products.nodes) return [];

    return collectionData.products.nodes
      .map(transformStorefrontProduct)
      .filter((p): p is TransformedProduct => p !== null);
  });

  const hasMore = computed(() => {
    return collection.value?.products.pageInfo.hasNextPage ?? false;
  });

  const endCursor = computed(() => {
    return collection.value?.products.pageInfo.endCursor ?? null;
  });

  const loadMore = async () => {
    if (!hasMore.value || !endCursor.value) return;

    await fetchMore({
      variables: {
        after: endCursor.value
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.collectionByHandle?.products.nodes) {
          return previousResult;
        }

        if (!previousResult.collectionByHandle?.products.nodes) {
          return fetchMoreResult;
        }

        // Merge previous and new products
        return {
          ...fetchMoreResult,
          collectionByHandle: {
            ...fetchMoreResult.collectionByHandle,
            products: {
              ...fetchMoreResult.collectionByHandle.products,
              nodes: [
                ...previousResult.collectionByHandle.products.nodes,
                ...fetchMoreResult.collectionByHandle.products.nodes
              ],
              pageInfo: fetchMoreResult.collectionByHandle.products.pageInfo
            }
          }
        };
      }
    });
  };

  return {
    products,
    collection,
    loading,
    error,
    refetch,
    hasMore,
    endCursor,
    loadMore
  };
};
